import { ReactNode, createContext, useContext } from 'react';
import { useBasketCalculateWithPolling } from '@flows/src/hooks/flow-products/useBasketCalculateWithPolling';
import { AnvaQuote, AnvaPackage } from '@flows/src/types/anva';
import { AnvaQuotesPayload } from '@flows/src/api/anva-flows/getAnvaQuotesV2';

interface JobState {
  jobId: string;
  status: string;
  isLoading: boolean;
  error: string | null;
  result?: AnvaQuote | AnvaPackage | null; // Package jobs return AnvaPackage, quote jobs return AnvaQuote
  packageCode?: string;
  isStandalone?: boolean;
  anvaProductId: string;
  index: number;
}

interface AnvaBasketCalculationContextType {
  jobStates: Record<string, JobState>;
  isCalculating: boolean;
  hasActiveJobs: boolean;
  calculate: (payload: AnvaQuotesPayload) => Promise<void>;
  error: Error | null;
  getJobForQuote: (
    anvaProductId: string,
    index: number,
    packageCode?: string,
  ) => JobState | null;
  isQuoteLoading: (
    anvaProductId: string,
    index: number,
    packageCode?: string,
  ) => boolean;
}

const BasketCalculationContext =
  createContext<AnvaBasketCalculationContextType | null>(null);

export const useBasketCalculation = () => {
  const context = useContext(BasketCalculationContext);
  return (
    context || {
      jobStates: {},
      isCalculating: false,
      hasActiveJobs: false,
      calculate: () => {
        return Promise.reject(
          new Error(
            'useBasketCalculation must be used within BasketCalculationProvider',
          ),
        );
      },
      error: null,
      getJobForQuote: () => null,
      isQuoteLoading: () => false,
    }
  );
};

interface Props {
  children: ReactNode;
  sessionId: string;
}

export const AnvaBasketCalculationProvider = ({
  children,
  sessionId,
}: Props) => {
  const { jobStates, isCalculating, calculate, error } =
    useBasketCalculateWithPolling(sessionId);

  const hasActiveJobs =
    isCalculating ||
    Object.values(jobStates).some((job) => job.isLoading === true);

  const getJobForQuote = (
    anvaProductId: string,
    index: number,
    packageCode?: string,
  ): JobState | null => {
    const jobState = Object.values(jobStates).find((job) => {
      if (packageCode) {
        // For package quotes, ONLY match jobs that belong to the SAME package
        // 1. Package-level jobs that affect all quotes in the package
        if (job.packageCode === packageCode && job.anvaProductId === '') {
          return true;
        }

        // 2. Individual quote jobs within the same package
        if (
          job.packageCode === packageCode &&
          job.anvaProductId === anvaProductId &&
          !job.isStandalone
        ) {
          return true;
        }

        // 3. Fallback: if we're actively calculating and there's a job for this specific package
        if (
          isCalculating &&
          job.packageCode === packageCode &&
          !job.isStandalone
        ) {
          return true;
        }
      } else {
        // For standalone insurances, match by anvaProductId and isStandalone flag
        if (job.anvaProductId === anvaProductId && job.isStandalone === true) {
          return true;
        }

        // Enhanced fallback: if we're calculating and there are standalone jobs,
        // match by anvaProductId but ONLY if it's standalone
        if (
          isCalculating &&
          job.isStandalone === true &&
          job.anvaProductId === anvaProductId
        ) {
          return true;
        }

        // Additional fallback: if there's only one standalone job running and we're looking for a standalone quote
        if (
          isCalculating &&
          job.isStandalone === true &&
          Object.values(jobStates).filter(
            (j) => j.isStandalone === true && j.isLoading,
          ).length === 1
        ) {
          return true;
        }
      }
      return false;
    });

    return jobState || null;
  };

  const isQuoteLoading = (
    anvaProductId: string,
    index: number,
    packageCode?: string,
  ): boolean => {
    // Check if there's a specific job for this quote
    const jobState = getJobForQuote(anvaProductId, index, packageCode);

    // 1. If we found a matching job state return its loading flag directly
    if (jobState) {
      return jobState.isLoading;
    }

    // 2. If we are still in the very first phase (we triggered calculate but have not received
    //    the initial job list yet) fall back to the global isCalculating flag.
    //    This is identified by `isCalculating` being true **and** `jobStates` being empty.
    if (isCalculating && Object.keys(jobStates).length === 0) {
      return true;
    }

    // 3. Package-level fallback: check if there are still running jobs for the SAME package ONLY
    if (packageCode) {
      return Object.values(jobStates).some(
        (job) =>
          job.isLoading && job.packageCode === packageCode && !job.isStandalone,
      );
    }

    // 4. Stand-alone insurance fallback: check if there are running jobs that match this product id AND are standalone
    return Object.values(jobStates).some(
      (job) =>
        job.isLoading &&
        job.isStandalone === true &&
        job.anvaProductId === anvaProductId,
    );
  };

  const contextValue: AnvaBasketCalculationContextType = {
    jobStates,
    isCalculating,
    hasActiveJobs,
    calculate,
    error,
    getJobForQuote,
    isQuoteLoading,
  };

  return (
    <BasketCalculationContext.Provider value={contextValue}>
      {children}
    </BasketCalculationContext.Provider>
  );
};
